import type { Signal } from "@preact/signals";
import type { JSX } from "preact";
import { createContext } from "preact";
import { useContext, useEffect, useRef } from "preact/hooks";

export type FormErrors = Signal<Record<string, string>>;

const FormContext = createContext<FormErrors | null>(null);

/**
 * Returns the form errors signal from the nearest Form ancestor.
 * Returns null if not inside a Form with errors.
 */
export function useFormErrors(): FormErrors | null {
  return useContext(FormContext);
}

/**
 * Clears a specific field's error from the form errors signal.
 */
export function clearFieldError(
  errors: FormErrors | null,
  fieldName: string | undefined,
) {
  if (!errors || !fieldName || !errors.value[fieldName]) return;
  const { [fieldName]: _, ...rest } = errors.value;
  errors.value = rest;
}

type FormProps = Omit<JSX.IntrinsicElements["form"], "onSubmit"> & {
  /**
   * Signal containing field errors as { [fieldName]: errorMessage }.
   * Passed to children via context so inputs can display and clear their errors.
   */
  errors?: FormErrors;
  onSubmit?: JSX.GenericEventHandler<HTMLFormElement>;
};

export function Form({ children, errors, onSubmit, ...props }: FormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const hasInitialized = useRef(false);

  // Compute whether there are any errors for the dependency
  const hasErrors = errors ? Object.keys(errors.value).length > 0 : false;

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const form = formRef.current;
    const el =
      form?.querySelector<HTMLElement>('[aria-invalid="true"]') ||
      form?.querySelector<HTMLElement>("[autofocus]");
    el?.focus();
  }, [hasErrors]);

  return (
    <FormContext.Provider value={errors ?? null}>
      <form
        ref={formRef}
        onSubmit={(e) => {
          hasInitialized.current = false;
          onSubmit?.(e);
        }}
        {...props}
      >
        {children}
      </form>
    </FormContext.Provider>
  );
}
